import React, { useCallback, useEffect, useState } from 'react';
import '../../../style/myContent.css';
import CreateQuestionSetModel from '../components/CreateQuestionSetModel';
import CustomToast from '../../../../../components/Toast';
import { useGetQuestionSetsMutation, useGetCanShowDeletedMutation } from '../../../../../features/authentication/qSetApiSlice';
import { languages } from '../../../utils/contants';
import { useGetShowDeletedTeacherSetsMutation, useUpdateShowDeletedTeacherSetsMutation } from '../../../../../features/authentication/persistApiSlice';
import SkeletonGrid from '../../../../../components/SkeletonLoader/SkeletonGrid';
import SetListing from '../../../components/SetListing';
import EmptySet from '../../../components/EmptySet';
import ActivityTypes from '../../../../../enums/activityTypes';

const QuestionSets = () => {
  const [updateShowDeletedSetsPersist] = useUpdateShowDeletedTeacherSetsMutation();
  const [openCreateQuestion, setOpenCreateQuestion] = useState(false);
  const [questionSets, setQuestionSets] = useState(null);
  const [skip, setSkip] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const takeSize = 9;
  const [showDeletedSets, setShowDeletedSets] = useState(null);
  const [showDisplayDeletedSetCheckbox, setShowDisplayDeletedSetCheckbox] = useState(false);

  const [getQuestionSets] = useGetQuestionSetsMutation();
  const [getShowDeletedSetsPersist] = useGetShowDeletedTeacherSetsMutation();
  const [getCanShowDeleted] = useGetCanShowDeletedMutation();

  const getShowDeletedPersistAsync = useCallback(async () => {
    let response = await getShowDeletedSetsPersist().unwrap();
    setShowDeletedSets(response);
    return response;
  }, [getShowDeletedSetsPersist]);

  const getQuestionSetsAsync = useCallback(async (skip, take, searchText, showDeletedSets) => {
    setIsLoading(true);
    try {
      const options = {
        skip: skip,
        take: take,
        searchString: searchText,
        activityTypes: [ActivityTypes.Assignment, ActivityTypes.ConstructedResponse, ActivityTypes.DBQ, ActivityTypes.ShortAnswer],
        showDeleted: showDeletedSets
      };

      let response = await getQuestionSets(options);
      if (response.error) {
        CustomToast(response.error.data.error, 'error');
      }
      else {
        let updatedQuestionSets = response.data.data.map(item => ({
          ...item,
          languageId: languages[0]?.id
        }));

        setQuestionSets(updatedQuestionSets);
        setPageCount(Math.ceil(response.data.total / takeSize));
      }
    } catch (error) {
      console.log(error);
    }
    setIsLoading(false);
  }, [getQuestionSets, takeSize]);

  const getShowDisplayDeletedSetCheckboxAsync = useCallback(async () => {
    let response = await getCanShowDeleted().unwrap();
    setShowDisplayDeletedSetCheckbox(response.canShowDeleted);
    return response;
  }, [getCanShowDeleted]);

  useEffect(() => {
    const initializeQuestionSets = async () => {
      const showDeletedResponse = await getShowDeletedPersistAsync();
      await getShowDisplayDeletedSetCheckboxAsync();
      await getQuestionSetsAsync(0, takeSize, '', showDeletedResponse);
    };

    initializeQuestionSets();
  }, [getShowDeletedPersistAsync, getShowDisplayDeletedSetCheckboxAsync, getQuestionSetsAsync, takeSize]);

  const handleShowDeletedChangesAsync = async () => {
    setShowDeletedSets(!showDeletedSets);
    let options = {
      showDeleted: !showDeletedSets
    };
    await updateShowDeletedSetsPersist(options);
  };

  const handleCreateSet = () => {
    setOpenCreateQuestion(true);
  };

  return (
    <>
      {isLoading || questionSets == null ?
        <SkeletonGrid
          rowCount={3}
          colCount={3}
          className={'h-150 mb-3'}
        />
        :
        searchText.length > 0 || questionSets?.length > 0 ?
          <SetListing
            setSetsList={setQuestionSets}
            setsList={questionSets}
            setOpenCreateSet={setOpenCreateQuestion}
            setSearchText={setSearchText}
            searchText={searchText}
            pageCount={pageCount}
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            getSets={getQuestionSetsAsync}
            setSkip={setSkip}
            takeSize={takeSize}
            showDeletedSets={showDeletedSets}
            handleShowDeletedChanges={handleShowDeletedChangesAsync}
            showDisplayDeletedSetCheckbox={showDisplayDeletedSetCheckbox}
          />
          :
          <EmptySet
            handleCreateSet={handleCreateSet}
            showDeletedSets={showDeletedSets}
            handleShowDeletedChanges={handleShowDeletedChangesAsync}
            showDisplayDeletedSetCheckbox={showDisplayDeletedSetCheckbox}
            onImportSuccess={() => getQuestionSetsAsync(skip, takeSize, searchText, showDeletedSets)}
          />
      }

      <CreateQuestionSetModel
        isOpen={openCreateQuestion}
        onClose={() => setOpenCreateQuestion(false)}
        showQuestionOptions={true}
      />
    </>
  );
};

export default QuestionSets;